import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, safeImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  Youtube,
  Play,
  Eye,
  Users,
  Video as VideoIcon,
  Sparkles,
  ArrowUpRight,
  Mail,
  Settings,
  TrendingUp,
  Wand2,
  Copy,
  Loader2,
  Twitter,
  Instagram,
  Music,
  Crown,
  DollarSign,
  Check,
  Zap,
  Clock,
  Lock,
  Plane,
  BedDouble,
  Share2,
  Download,
} from "lucide-react";

const SESSION_KEY = "rr_session_id";
function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function formatNumber(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}

function openExternal(e, url) {
  if (e) e.preventDefault();
  let safe = url || "";
  if (safe && !/^https?:\/\//i.test(safe)) safe = "https://" + safe;
  window.open(safe, "_blank", "noopener,noreferrer");
}

const HERO_BRANDS = [
  { name: "Amazon", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/pxclwdxp_amazon-logo-png_seeklogo-286206.png", url: "https://amzn.to/4xYY1QW" },
  { name: "Flipkart", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/z8xsya0l_Flipkart-Logo-2015-present.webp", url: "https://fkrt.co/9ggBlN" },
  { name: "Myntra", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/n209cphq_myntra-logo-png_seeklogo-513444.png", url: "https://myntr.in/8JVrkJ" },
  { name: "AJIO", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/4csemkxn_Ajio-Logo1.jpg", url: "https://extp.in/iS45T8" },
  { name: "Oppo", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/kclcbyc6_oppo-logo-oppo-icon-free-free-vector.jpg", url: "https://extp.in/6sY1C1" },
  { name: "Nykaa Fashion", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/rmvqnvnt_images.jpg", url: "https://extp.in/Wh5IDv" },
  { name: "Palmonas", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/18tgqov6_unnamed%20%281%29.png", url: "https://extp.in/ypA7HV" },
  { name: "Cleartrip", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/aaxe939l_cleartrip-logo-png_seeklogo-498837.png", url: "https://extp.in/DfKzWO" },
  { name: "Yatra", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/ssz3dbtx_yatra-com-logo-png_seeklogo-362510.png", url: "https://extp.in/qbiCpa" },
  { name: "Skyscanner", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/u13hvhju_unnamed.png", url: "https://extp.in/SNgwd3" },
  { name: "Plum", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/x4b7c4l5_Plum.jpeg", url: "https://extp.in/e93P9R" },
  { name: "Meesho", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/othlnjyu_images%20%281%29.jpg", url: "https://bitli.in/fiMilUa" },
];

function BrandLogoStrip() {
  return (
    <div
      className="mb-6 flex flex-wrap gap-2 sm:gap-3 fade-up"
      style={{ animationDelay: "0s" }}
      data-testid="hero-brand-strip"
    >
      {HERO_BRANDS.map((b) => (
        <a
          key={b.name}
          href={b.url}
          onClick={(e) => openExternal(e, b.url)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white flex items-center justify-center p-2 hover:scale-110 hover:shadow-[0_0_20px_rgba(230,255,0,0.35)] transition-all cursor-pointer"
          aria-label={b.name}
          title={b.name}
          data-testid={`hero-brand-${b.name.toLowerCase().replace(/\s/g, "")}`}
        >
          <img
            src={safeImageUrl(b.logo)}
            alt={b.name}
            className="max-w-full max-h-full object-contain"
          />
        </a>
      ))}
    </div>
  );
}

function Header({ settings }) {
  return (
    <header
      className="glass sticky top-0 z-50 px-6 lg:px-12 py-4 flex items-center justify-between"
      data-testid="site-header"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#FF0000] flex items-center justify-center">
          <Play size={18} fill="white" stroke="white" />
        </div>
        <span className="font-display font-extrabold text-lg tracking-tight" data-testid="brand-name">
          {(settings?.creator_name || "Creator Hub").toUpperCase()}
        </span>
      </div>
      <nav className="hidden md:flex items-center gap-2 text-sm">
        <a href="#videos" className="px-4 py-2 rounded-full bg-[#E6FF00] border border-[#E6FF00] text-black hover:brightness-95 hover:scale-105 transition-all font-bold" data-testid="nav-videos">Videos</a>
        <a href="#gear" className="px-4 py-2 rounded-full bg-[#E6FF00] border border-[#E6FF00] text-black hover:brightness-95 hover:scale-105 transition-all font-bold" data-testid="nav-gear">Gear</a>
        <a href="#ai" className="px-4 py-2 rounded-full bg-[#E6FF00] border border-[#E6FF00] text-black hover:brightness-95 hover:scale-105 transition-all font-bold" data-testid="nav-ai">Recipe AI</a>
        <a href="#stats" className="px-4 py-2 rounded-full bg-[#E6FF00] border border-[#E6FF00] text-black hover:brightness-95 hover:scale-105 transition-all font-bold" data-testid="nav-stats">Stats</a>
        <a href="#subscription" className="hidden px-4 py-2 rounded-full bg-[#E6FF00] border border-[#E6FF00] text-black hover:brightness-95 hover:scale-105 transition-all font-bold" data-testid="nav-subscription">Subscription</a>
      </nav>
      <a
        href={settings?.youtube_url || "https://youtube.com"}
        onClick={(e) => openExternal(e, settings?.youtube_url || "https://youtube.com")}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-red text-sm cursor-pointer"
        data-testid="header-subscribe-btn"
      >
        <Youtube size={16} /> Subscribe
      </a>
    </header>
  );
}

function Hero({ stats, settings }) {
  return (
    <section className="relative px-6 lg:px-12 pt-16 lg:pt-24 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <BrandLogoStrip />
          <div className="flex items-center gap-3 mb-6 fade-up" style={{ animationDelay: "0s" }}>
            <span className="live-dot"></span>
            <span className="overline">Now uploading weekly</span>
          </div>
          <h1
            className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-[5.5rem]"
            data-testid="hero-headline"
          >
            Cook <span className="text-[#FF0000]">bold.</span>
            <br />
            Eat <span className="text-[#E6FF00]">rustic.</span>
          </h1>
          <p
            className="mt-8 text-lg text-[#a1a1aa] leading-relaxed max-w-xl fade-up"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-bio"
          >
            {settings?.creator_bio}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "0.2s" }}>
            <a
              href={settings?.youtube_url || "https://youtube.com"}
              onClick={(e) => openExternal(e, settings?.youtube_url || "https://youtube.com")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-red cursor-pointer"
              data-testid="hero-subscribe-btn"
            >
              <Youtube size={18} /> Subscribe on YouTube
            </a>
            <a
              href={(settings?.youtube_url || "https://youtube.com") + "/join"}
              onClick={(e) => openExternal(e, (settings?.youtube_url || "https://youtube.com") + "/join")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-yellow cursor-pointer"
              data-testid="hero-membership-btn"
            >
              <Crown size={18} /> Paid Subscription
            </a>
            <a
              href="https://app.smmowl.com/ref/44751"
              onClick={(e) => openExternal(e, "https://app.smmowl.com/ref/44751")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost cursor-pointer"
              data-testid="hero-monetize-btn"
            >
              <DollarSign size={16} /> Monetize Your Channel
            </a>
            <a href="#gear" className="btn-ghost" data-testid="hero-gear-btn">
              See my gear <ArrowUpRight size={16} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
            <img
              src={safeImageUrl("https://customer-assets.emergentagent.com/job_subscriber-surge-23/artifacts/1rn210pu_ChatGPT%20Image%20Jun%2028%2C%202026%2C%2001_10_15%20PM.png")}
              alt="RUSTIC RASOI Kitchen"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
          </div>

          {/* Floating bento stat */}
          <div
            className="absolute -bottom-6 -left-6 glass p-5 rounded-xl glow-red"
            data-testid="hero-stat-subs"
          >
            <div className="overline mb-1">Subscribers</div>
            <div className="font-display font-extrabold text-3xl">{formatNumber(stats?.subscribers || 0)}</div>
          </div>
          <div
            className="absolute -top-6 -right-2 glass p-5 rounded-xl"
            data-testid="hero-stat-views"
          >
            <div className="overline mb-1" style={{ color: "#E6FF00" }}>Total Views</div>
            <div className="font-display font-extrabold text-2xl">{formatNumber(stats?.total_views || 0)}</div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="max-w-7xl mx-auto mt-24">
        <div className="marquee py-6 border-y border-white/10">
          <div className="marquee-track">
            {["FRESH FROM THE KITCHEN", "AUTHENTIC RECIPES", "BOLD SPICES", "ROOTS & FLAVOURS", "COOK WITH SOUL", "TASTE TRADITION", "MADE WITH LOVE", "FRESH FROM THE KITCHEN", "AUTHENTIC RECIPES", "BOLD SPICES", "ROOTS & FLAVOURS", "COOK WITH SOUL", "TASTE TRADITION", "MADE WITH LOVE"].map((t, i) => (
              <span key={i} className="font-display font-extrabold text-3xl text-white/80 whitespace-nowrap">
                {t} <span className="text-[#FF0000]">●</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const FIN_CATEGORIES = [
  { key: "banking", label: "Banking Partners", slots: 5 },
  { key: "creditcard", label: "Credit Cards", slots: 10 },
  { key: "loan", label: "Loans", slots: 4 },
  { key: "savings", label: "Zero Balance Savings Account", slots: 2 },
];

function FinancialPartnersSection({ partners }) {
  const byCat = FIN_CATEGORIES.reduce((acc, c) => {
    acc[c.key] = partners.filter((p) => p.category === c.key).sort((a, b) => a.sort_order - b.sort_order);
    return acc;
  }, {});

  return (
    <section
      id="financial-partners"
      className="px-6 lg:px-12 py-16 border-t border-white/10"
      data-testid="financial-partners-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="overline mb-3">Trusted network</div>
          <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter" data-testid="financial-heading">
            Banking Partners, <span className="text-[#E6FF00]">Credit Cards</span> & <span className="text-[#FF0000]">Loans</span>
          </h2>
        </div>

        <div className="space-y-10">
          {FIN_CATEGORIES.map((cat) => {
            const items = byCat[cat.key] || [];
            const totalSlots = cat.slots;
            const filled = items.slice(0, totalSlots);
            const empty = Math.max(0, totalSlots - filled.length);
            return (
              <div key={cat.key} data-testid={`fin-cat-${cat.key}`}>
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">{cat.label}</h3>
                  <span className="text-xs text-[#71717a] font-mono">{filled.length} / {totalSlots}</span>
                </div>
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  {filled.map((p) => (
                    <a
                      key={p.id}
                      href={p.target_url || "#"}
                      onClick={(e) => (p.target_url ? openExternal(e, p.target_url) : e.preventDefault())}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-24 h-16 sm:w-28 sm:h-20 rounded-lg bg-white flex items-center justify-center p-3 hover:scale-105 hover:shadow-[0_0_20px_rgba(230,255,0,0.35)] transition-all cursor-pointer"
                      title={p.name}
                      data-testid={`fin-${cat.key}-${p.id}`}
                    >
                      <img src={safeImageUrl(p.logo_url)} alt={p.name} className="max-w-full max-h-full object-contain" />
                    </a>
                  ))}
                  {Array.from({ length: empty }).map((_, i) => (
                    <div
                      key={`e-${i}`}
                      className="w-24 h-16 sm:w-28 sm:h-20 rounded-lg border border-dashed border-white/15 flex items-center justify-center text-[10px] text-[#4a4a52] font-mono uppercase tracking-wider"
                      data-testid={`fin-empty-${cat.key}-${i}`}
                    >
                      Coming Soon
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// Shared travel partner data — fetched from /api/travel-partners with hardcoded fallback for offline dev
const DEFAULT_AIRLINES = [
  { name: "Air India", target_url: "https://bitli.in/r713KCl", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/737oimhq_air-india-newlogo-red.jpg", deal: "Save upto 20%", tagline: "India's flag carrier — non-stop to the world." },
  { name: "Malaysia Airlines", target_url: "https://bitli.in/Xf8stS7", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/irylynfm_images%20%283%29.png", deal: "Save upto 20%", tagline: "Fly Kuala Lumpur & beyond in Malaysian hospitality." },
  { name: "Etihad Airways", target_url: "https://bitli.in/33Xhcbr", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/0mcuace2_etihad-airways-logo-png_seeklogo-177330.png", deal: "Save upto 20%", tagline: "Luxury from Abu Dhabi to 70+ global destinations." },
  { name: "Qatar Airways", target_url: "https://bitli.in/SbTY8AY", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/ye835vil_634083-QR-Logo-Horizontal-White-92a7ae-medium-1765966477.jpg", deal: "Save upto 20%", tagline: "World's best airline — award-winning cabin experience." },
  { name: "Air India Express", target_url: "https://extp.in/VeEvGX", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/qaz84ntv_Air-India-Express-Logo-300x300.jpg", deal: "Save upto 20%", tagline: "Fly light, fly quick — smart short-haul fares." },
  { name: "Akasa Air", target_url: "https://extp.in/E87Xd1", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/esuz0uly_Akasa_Air_logo_with_slogan.webp", deal: "Save upto 20%", tagline: "It's your sky — India's freshest budget airline." },
];

const DEFAULT_HOTELS = [
  { name: "Booking.com", target_url: "https://bitli.in/NUR1mxC", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/fvqwjoaa_800_booking_com_logotype_aug2020_white.jpg", deal: "Save upto 60%", tagline: "28M+ stays worldwide — genius members save more." },
  { name: "Expedia", target_url: "https://bitli.in/Y7zHv71", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/c0orpqpb_Expedia-Logo.webp", deal: "Save upto 60%", tagline: "Bundle flights + hotels and save on every trip." },
  { name: "Agoda", target_url: "https://bitli.in/txFpKrc", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/gxfdp72z_images%20%284%29.png", deal: "Save upto 60%", tagline: "Asia's home ground — unbeatable rates on stays." },
  { name: "Goibibo", target_url: "https://bitli.in/CQnkfRC", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/gphd9i25_goibibo-logo-png_seeklogo-305530.png", deal: "Save upto 60%", tagline: "GoCash + coupon codes for extra savings." },
  { name: "Cleartrip", target_url: "https://bitli.in/k6Iuivj", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/aaxe939l_cleartrip-logo-png_seeklogo-498837.png", deal: "Save upto 60%", tagline: "Fast search, cleaner booking, best price promise." },
  { name: "MakeMyTrip", target_url: "https://bitli.in/R5RSBTA", logo_url: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/8dzft6r1_makemytrip-logo-png_seeklogo-336111.png", deal: "Save upto 60%", tagline: "India's #1 trip planner — flights, hotels, holidays." },
];

// Rotating spotlight picks by ISO week of year — deterministic weekly rotation
function getWeeklyIndex(len) {
  if (!len) return 0;
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const week = Math.floor(((now - start) / 86400000 + start.getDay() + 1) / 7);
  return week % len;
}

function FeaturedSpotlight({ items, category, testid }) {
  if (!items?.length) return null;
  const pinned = items.find((x) => x.featured);
  const p = pinned || items[getWeeklyIndex(items.length)];
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#E6FF00]/30 bg-gradient-to-br from-[#E6FF00]/10 via-black to-black p-6 sm:p-8 mb-6 glow-yellow"
      data-testid={testid}
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#E6FF00] rounded-full blur-[120px] opacity-20 pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row items-center gap-5 sm:gap-7">
        <div className="w-32 h-20 sm:w-40 sm:h-24 shrink-0 rounded-lg bg-white flex items-center justify-center p-3">
          {p.logo_url ? (
            <img src={safeImageUrl(p.logo_url)} alt={p.name} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-center font-display font-extrabold text-base text-black leading-tight tracking-tight px-1">{p.name}</span>
          )}
        </div>
        <div className="flex-1 text-center sm:text-left">
          <div className="overline mb-2 inline-flex items-center gap-2">
            <Sparkles size={14} className="text-[#E6FF00]" /> Weekly spotlight · {category}
          </div>
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight" data-testid={`${testid}-name`}>
            {p.name}
          </h3>
          {p.tagline && <p className="mt-2 text-sm text-[#e5e5e5] max-w-xl">{p.tagline}</p>}
          {p.deal && (
            <span className="inline-flex items-center gap-1 mt-3 px-3 py-1 rounded-full bg-[#FF0000] text-white text-xs font-mono font-bold tracking-wider uppercase">
              {p.deal}
            </span>
          )}
        </div>
        <a
          href={p.target_url}
          onClick={(e) => openExternal(e, p.target_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-yellow shrink-0"
          data-testid={`${testid}-cta`}
        >
          Book on {p.name} <ArrowUpRight size={16} />
        </a>
      </div>
    </div>
  );
}

function TravelGridCard({ item, testid }) {
  return (
    <a
      href={item.target_url}
      onClick={(e) => openExternal(e, item.target_url)}
      target="_blank"
      rel="noopener noreferrer"
      className="relative aspect-[5/3] rounded-lg bg-white flex items-center justify-center p-3 hover:scale-[1.04] hover:shadow-[0_0_24px_rgba(230,255,0,0.35)] transition-all cursor-pointer group"
      title={item.name}
      data-testid={testid}
    >
      {item.logo_url ? (
        <img src={safeImageUrl(item.logo_url)} alt={item.name} className="max-w-full max-h-full object-contain" />
      ) : (
        <span className="text-center font-display font-extrabold text-sm sm:text-base text-black leading-tight tracking-tight px-1">
          {item.name}
        </span>
      )}
      {item.deal && (
        <span className="absolute -top-2 -left-2 px-2 py-0.5 rounded-full bg-[#FF0000] text-white text-[9px] font-mono font-bold tracking-wider uppercase shadow-lg">
          {item.deal}
        </span>
      )}
      <span className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowUpRight size={14} className="text-black/70" />
      </span>
    </a>
  );
}

function TravelPartnersSection({ airlines }) {
  return (
    <section
      id="travel-partners"
      className="px-6 lg:px-12 py-16 border-t border-white/10"
      data-testid="travel-partners-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <Plane size={14} className="text-[#E6FF00]" /> Fly deals
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter" data-testid="travel-heading">
              Travel <span className="text-[#E6FF00]">Partners</span>
            </h2>
            <p className="mt-3 text-sm text-[#a1a1aa] max-w-xl">
              Book flights on top domestic &amp; international airlines — direct affiliate links below.
            </p>
          </div>
          <span className="text-xs text-[#71717a] font-mono">{airlines.length} airlines</span>
        </div>

        <FeaturedSpotlight items={airlines} category="Airlines" testid="airline-spotlight" />

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {airlines.map((a) => (
            <TravelGridCard
              key={a.id || a.name}
              item={a}
              testid={`travel-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function HotelsPartnersSection({ hotels }) {
  return (
    <section
      id="hotels-partners"
      className="px-6 lg:px-12 py-16 border-t border-white/10"
      data-testid="hotels-partners-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="overline mb-3 flex items-center gap-2">
              <BedDouble size={14} className="text-[#E6FF00]" /> Stay deals
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter" data-testid="hotels-heading">
              Hotels, Stays <span className="text-[#E6FF00]">&amp;</span> <span className="text-[#FF0000]">Deals</span>
            </h2>
            <p className="mt-3 text-sm text-[#a1a1aa] max-w-xl">
              Book hotels &amp; getaways on India&apos;s top travel platforms — tap any brand to save.
            </p>
          </div>
          <span className="text-xs text-[#71717a] font-mono">{hotels.length} partners</span>
        </div>

        <FeaturedSpotlight items={hotels} category="Hotels & Stays" testid="hotel-spotlight" />

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {hotels.map((h) => (
            <TravelGridCard
              key={h.id || h.name}
              item={h}
              testid={`hotel-${h.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FlyStayTicker({ airlines, hotels }) {
  const renderRow = (items, testidPrefix, reverse) => {
    if (!items?.length) return null;
    const loop = [...items, ...items, ...items, ...items];
    return (
      <div className="marquee py-1">
        <div className="marquee-track" style={reverse ? { animationDirection: "reverse" } : undefined}>
          {loop.map((p, i) => (
            <a
              key={`${testidPrefix}-${i}`}
              href={p.target_url}
              onClick={(e) => openExternal(e, p.target_url)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={p.name}
              title={p.name}
              className="mx-3 sm:mx-4 shrink-0 w-40 h-20 sm:w-48 sm:h-24 rounded-lg bg-white flex items-center justify-center p-4 hover:scale-[1.05] hover:shadow-[0_0_28px_rgba(230,255,0,0.35)] transition-all cursor-pointer"
              data-testid={`${testidPrefix}-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`}
            >
              {p.logo_url ? (
                <img src={safeImageUrl(p.logo_url)} alt={p.name} className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-center font-display font-extrabold text-sm sm:text-base text-black leading-tight tracking-tight px-1">
                  {p.name}
                </span>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <section
      id="fly-stay-ticker"
      className="py-12 border-t border-white/10 overflow-hidden"
      data-testid="fly-stay-ticker-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center mb-8">
        <div className="overline mb-3 inline-flex items-center gap-2">
          <Plane size={14} className="text-[#E6FF00]" /> Travel deals ticker
        </div>
        <h2 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tighter" data-testid="fly-stay-heading">
          Fly <span className="text-[#E6FF00]">&amp;</span> Stay — <span className="text-[#FF0000]">Book smart.</span>
        </h2>
      </div>

      <div className="bg-black py-3 border-y border-white/10" data-testid="ticker-airlines-marquee">
        {renderRow(airlines, "ticker-air", false)}
      </div>
      <div className="bg-black py-3 border-y border-white/10 mt-4" data-testid="ticker-hotels-marquee">
        {renderRow(hotels, "ticker-hotel", true)}
      </div>
    </section>
  );
}


function VideosGrid({ videos, isDemo }) {
  const handleVideoClick = (e, url) => {
    e.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <section id="videos" className="px-6 lg:px-12 py-24" data-testid="videos-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <div className="overline mb-3">Latest uploads</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="videos-heading">
              Fresh from the kitchen
            </h2>
          </div>
          {isDemo && (
            <span className="text-xs text-[#71717a] font-mono" data-testid="demo-badge-videos">
              [ DEMO DATA — Add your channel in Admin ]
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((v, i) => (
            <a
              key={v.id}
              href={v.url}
              onClick={(e) => handleVideoClick(e, v.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="card-tech overflow-hidden group fade-up cursor-pointer"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`video-card-${i}`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={safeImageUrl(v.thumbnail)}
                  alt={v.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#FF0000] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Play size={28} fill="white" stroke="white" />
                  </div>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display font-semibold text-lg leading-snug group-hover:text-[#FF0000] transition-colors line-clamp-2">
                  {v.title}
                </h3>
                <div className="mt-3 flex items-center gap-4 text-sm text-[#71717a]">
                  <span className="flex items-center gap-1"><Eye size={14} /> {formatNumber(v.view_count)}</span>
                  <span>{new Date(v.published_at).toLocaleDateString()}</span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function AffiliateShowcase({ affiliates }) {
  const handleClick = async (link) => {
    try {
      api.post(`/affiliates/${link.id}/track`);
    } catch (e) {
      // non-blocking
    }
    window.open(link.target_url, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="gear" className="px-6 lg:px-12 py-24 border-t border-white/10" data-testid="gear-section">
      <div className="max-w-7xl mx-auto">
          <div>
            <div className="overline mb-3">Creator gear</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-2xl" data-testid="gear-heading">
              The kitchen tools I actually use. <span className="text-[#E6FF00]">Tested & loved.</span>
            </h2>
          </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {affiliates.map((a, i) => (
            <button
              key={a.id}
              onClick={() => handleClick(a)}
              className="card-tech overflow-hidden text-left group fade-up"
              style={{ animationDelay: `${i * 0.07}s` }}
              data-testid={`affiliate-card-${i}`}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={safeImageUrl(a.image_url)}
                  alt={a.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {a.badge && (
                  <span className="absolute top-3 left-3 bg-[#E6FF00] text-black text-[10px] font-bold px-2 py-1 rounded-sm tracking-widest">
                    {a.badge}
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold text-lg leading-tight">{a.title}</h3>
                  <ArrowUpRight size={18} className="text-[#71717a] group-hover:text-[#FF0000] transition-colors flex-shrink-0" />
                </div>
                <p className="mt-2 text-sm text-[#a1a1aa] line-clamp-2">{a.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-sm text-[#E6FF00]">{a.price || "View"}</span>
                  <span className="text-xs text-[#71717a]" data-testid={`affiliate-clicks-${i}`}>
                    {a.clicks} clicks
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// Theme palettes for the 1080x1920 Instagram Story card
const STORY_THEMES = {
  dark: {
    label: "Dark",
    bg: "linear-gradient(180deg, #050505 0%, #0a0a0a 50%, #1a0000 100%)",
    exportBg: "#050505",
    text: "#ffffff",
    muted: "#a1a1aa",
    subtle: "#71717a",
    body: "#e5e5e5",
    accent: "#E6FF00",
    accentText: "#000000",
    logoBg: "#FF0000",
    logoText: "#ffffff",
    heroBg: "rgba(255,255,255,0.04)",
    heroBorder: "6px solid #E6FF00",
    thumbBg: "rgba(0,0,0,0.55)",
    thumbBorder: "1px solid rgba(255,255,255,0.1)",
    urlColor: "#E6FF00",
    tagline: "Get 5 viral recipe ideas at",
  },
  light: {
    label: "Light",
    bg: "linear-gradient(180deg, #FFFCF3 0%, #FFF7DE 50%, #FFEDB8 100%)",
    exportBg: "#FFFCF3",
    text: "#0a0a0a",
    muted: "#404040",
    subtle: "#71717a",
    body: "#1c1c1c",
    accent: "#FF0000",
    accentText: "#ffffff",
    logoBg: "#0a0a0a",
    logoText: "#E6FF00",
    heroBg: "rgba(0,0,0,0.05)",
    heroBorder: "6px solid #FF0000",
    thumbBg: "rgba(0,0,0,0.06)",
    thumbBorder: "1px solid rgba(0,0,0,0.12)",
    urlColor: "#FF0000",
    tagline: "5 viral recipe ideas at",
  },
  festive: {
    label: "Festive",
    bg: "linear-gradient(180deg, #4A0000 0%, #7A0F0F 40%, #C41E3A 100%)",
    exportBg: "#4A0000",
    text: "#FFF8DC",
    muted: "#FFD8A8",
    subtle: "#FFC8A2",
    body: "#FFEED4",
    accent: "#FFD700",
    accentText: "#4A0000",
    logoBg: "#FF6B35",
    logoText: "#FFF8DC",
    heroBg: "rgba(255,215,0,0.08)",
    heroBorder: "6px solid #FFD700",
    thumbBg: "rgba(0,0,0,0.35)",
    thumbBorder: "1px solid rgba(255,215,0,0.25)",
    urlColor: "#FFD700",
    tagline: "Get 5 viral recipe ideas at",
  },
};

// Off-screen Instagram Story 1080x1920 card renderer for AI recipe sharing
function RecipeStoryCard({ recipe, ingredient, index, innerRef, theme = "dark" }) {
  if (!recipe) return null;
  const t = STORY_THEMES[theme] || STORY_THEMES.dark;
  return (
    <div
      ref={innerRef}
      style={{
        position: "fixed",
        top: 0,
        left: "-9999px",
        width: "1080px",
        height: "1920px",
        background: t.bg,
        color: t.text,
        padding: "80px 70px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontFamily: "'Outfit', 'Inter', system-ui, sans-serif",
      }}
    >
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 40 }}>
          <div style={{ width: 72, height: 72, borderRadius: 18, background: t.logoBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: t.logoText, fontWeight: 900, fontSize: 34 }}>▶</span>
          </div>
          <div>
            <div style={{ fontSize: 22, letterSpacing: 4, color: t.muted, fontFamily: "'JetBrains Mono', monospace" }}>RUSTIC RASOI</div>
            <div style={{ fontSize: 30, fontWeight: 900, color: t.text }}>Recipe Ideas AI</div>
          </div>
        </div>

        <div style={{ padding: "14px 24px", borderRadius: 999, background: t.accent, color: t.accentText, display: "inline-block", fontWeight: 800, fontSize: 26, letterSpacing: 3, textTransform: "uppercase" }}>
          #{index + 1} · {recipe.hook}
        </div>

        <h1 style={{ fontSize: 96, fontWeight: 900, lineHeight: 1.05, marginTop: 40, letterSpacing: -2, color: t.text }}>
          {recipe.title}
        </h1>

        <div style={{ marginTop: 44, padding: 30, borderLeft: t.heroBorder, background: t.heroBg, borderRadius: 12 }}>
          <div style={{ fontSize: 20, color: t.accent, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 3, textTransform: "uppercase", marginBottom: 14 }}>THE HERO INGREDIENT</div>
          <div style={{ fontSize: 68, fontWeight: 900, color: t.text, textTransform: "capitalize" }}>{ingredient}</div>
        </div>

        <p style={{ fontSize: 32, lineHeight: 1.45, marginTop: 48, color: t.body }}>
          {recipe.description}
        </p>

        {recipe.thumbnail_idea && (
          <div style={{ marginTop: 40, padding: 26, background: t.thumbBg, border: t.thumbBorder, borderRadius: 16 }}>
            <div style={{ fontSize: 18, color: t.accent, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 }}>THUMBNAIL VIBE</div>
            <div style={{ fontSize: 26, color: t.body, lineHeight: 1.4 }}>{recipe.thumbnail_idea}</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, marginTop: 40 }}>
        <div style={{ fontSize: 26, color: t.muted, textAlign: "center" }}>{t.tagline}</div>
        <div style={{ fontSize: 44, fontWeight: 900, color: t.urlColor, letterSpacing: -1 }}>rusticrasoi.in</div>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 20, color: t.subtle, fontFamily: "'JetBrains Mono', monospace" }}>@RUSTICRASOI</span>
          <span style={{ fontSize: 20, color: t.subtle }}>·</span>
          <span style={{ fontSize: 20, color: t.subtle, fontFamily: "'JetBrains Mono', monospace" }}>YouTube</span>
        </div>
      </div>
    </div>
  );
}

async function shareRecipeAsImage(nodeRef, recipe, theme = "dark") {
  const node = nodeRef.current;
  if (!node) return;
  const t = STORY_THEMES[theme] || STORY_THEMES.dark;
  try {
    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: 1,
      backgroundColor: t.exportBg,
      width: 1080,
      height: 1920,
    });
    const blob = await (await fetch(dataUrl)).blob();
    const fileName = `rustic-rasoi-${(recipe.title || "recipe").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
    const file = new File([blob], fileName, { type: "image/png" });

    // Try Web Share API with files (mobile)
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: recipe.title,
          text: `${recipe.title} — get more recipe ideas at rusticrasoi.in`,
        });
        toast.success("Shared!");
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled
      }
    }
    // Fallback: download
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    link.click();
    toast.success("Recipe card downloaded — post it to Instagram Stories!");
  } catch (e) {
    console.error(e);
    toast.error("Could not generate share card. Try again.");
  }
}


function AiGenerator() {
  const [ingredient, setIngredient] = useState("");
  const [diet, setDiet] = useState("any");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sharingIdx, setSharingIdx] = useState(-1);
  const [themes, setThemes] = useState({}); // { [recipeIndex]: "dark" | "light" | "festive" }
  const storyRefs = useRef([]);
  const sessionId = getSessionId();

  const generate = async () => {
    if (!ingredient.trim()) {
      toast.error("Enter an ingredient first (e.g. paneer, chicken, potato)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/generate", { ingredient, diet, session_id: sessionId });
      setResult(data);
      toast.success(`${data.recipes?.length || 0} viral recipe ideas ready!`);
    } catch (e) {
      toast.error("Generation failed. Try a different ingredient.");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const suggestions = ["Paneer", "Chicken", "Potato", "Eggs", "Mushroom", "Tomato"];

  return (
    <section
      id="ai"
      className="relative px-6 lg:px-12 py-24 border-t border-white/10 overflow-hidden"
      data-testid="ai-section"
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/18337643/pexels-photo-18337643.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/90 to-black" />

      <div className="relative max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles size={20} className="text-[#E6FF00]" />
          <span className="overline">Recipe AI</span>
        </div>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-3xl" data-testid="ai-heading">
          Got one ingredient? Get <span className="text-[#E6FF00]">5 viral recipes.</span>
        </h2>
        <p className="mt-5 text-[#a1a1aa] max-w-2xl text-lg">
          Type any ingredient in your fridge — Rustic Rasoi&apos;s AI kitchen brainstorms 5 addictive Indian recipe ideas, complete with hooks and thumbnail concepts.
        </p>

        <div className="glass mt-8 p-6 rounded-2xl" data-testid="ai-form">
          <label className="overline block mb-2">Hero ingredient</label>
          <input
            value={ingredient}
            onChange={(e) => setIngredient(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !loading) generate(); }}
            placeholder="e.g. paneer, chicken, potato, mushroom..."
            className="input-dark"
            data-testid="ai-ingredient-input"
          />
          <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => setIngredient(s)}
                className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-[#a1a1aa] hover:bg-[#E6FF00] hover:text-black hover:border-[#E6FF00] transition"
                data-testid={`ai-suggestion-${s.toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "any", label: "Any" },
                { key: "veg", label: "Veg" },
                { key: "non-veg", label: "Non-veg" },
                { key: "quick", label: "Under 30 min" },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setDiet(s.key)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    diet === s.key
                      ? "bg-[#E6FF00] text-black"
                      : "bg-white/5 text-[#a1a1aa] hover:bg-white/10"
                  }`}
                  data-testid={`ai-diet-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <button
              onClick={generate}
              disabled={loading}
              className="btn-yellow"
              data-testid="ai-generate-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
              {loading ? "Cooking up ideas..." : "Generate 5 recipes"}
            </button>
          </div>
        </div>

        {result && result.recipes && (
          <div className="mt-10 space-y-5 fade-up" data-testid="ai-results">
            {/* Hidden 1080x1920 story cards for share rendering */}
            {result.recipes.map((r, i) => (
              <RecipeStoryCard
                key={`story-${i}`}
                recipe={r}
                ingredient={result.ingredient}
                index={i}
                theme={themes[i] || "dark"}
                innerRef={(el) => { storyRefs.current[i] = el; }}
              />
            ))}
            <div className="flex items-baseline gap-3">
              <div className="overline">5 recipe ideas with</div>
              <span className="text-[#E6FF00] font-display font-bold text-xl">{result.ingredient}</span>
            </div>
            {result.recipes.map((r, i) => (
              <div
                key={i}
                className="card-tech p-6 hover:border-[#E6FF00]/40 transition"
                data-testid={`ai-recipe-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-[#E6FF00] font-mono text-sm">#{i + 1}</span>
                      <span className="px-2 py-0.5 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/30 text-[#FF0000] text-xs font-mono uppercase tracking-wider">{r.hook}</span>
                    </div>
                    <h3 className="font-display font-bold text-2xl leading-tight" data-testid={`ai-recipe-title-${i}`}>
                      {r.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => copy(`${r.title}\n\n${r.description}\n\nThumbnail: ${r.thumbnail_idea}`)}
                    className="shrink-0 text-[#71717a] hover:text-[#E6FF00] p-2"
                    aria-label="Copy recipe idea"
                    data-testid={`ai-recipe-copy-${i}`}
                  >
                    <Copy size={18} />
                  </button>
                </div>
                <p className="mt-4 text-[#e5e5e5] leading-relaxed" data-testid={`ai-recipe-desc-${i}`}>
                  {r.description}
                </p>
                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-black/40 border border-white/5">
                    <div className="overline mb-2 text-[#E6FF00]">Thumbnail concept</div>
                    <p className="text-sm text-[#a1a1aa]" data-testid={`ai-recipe-thumb-${i}`}>{r.thumbnail_idea}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-black/40 border border-white/5">
                    <div className="overline mb-2 text-[#E6FF00]">SEO tags</div>
                    <div className="flex flex-wrap gap-1.5" data-testid={`ai-recipe-tags-${i}`}>
                      {(r.tags || []).map((tag, j) => (
                        <span key={j} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-[#a1a1aa]">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-[#71717a] font-mono uppercase tracking-wider">Theme</span>
                    {Object.entries(STORY_THEMES).map(([key, cfg]) => {
                      const active = (themes[i] || "dark") === key;
                      const swatch = key === "dark"
                        ? "linear-gradient(135deg, #050505 0%, #1a0000 100%)"
                        : key === "light"
                        ? "linear-gradient(135deg, #FFFCF3 0%, #FFEDB8 100%)"
                        : "linear-gradient(135deg, #4A0000 0%, #C41E3A 100%)";
                      return (
                        <button
                          key={key}
                          onClick={() => setThemes((t) => ({ ...t, [i]: key }))}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-mono transition border ${
                            active
                              ? "bg-[#E6FF00] text-black border-[#E6FF00]"
                              : "bg-white/5 text-[#a1a1aa] border-white/10 hover:bg-white/10"
                          }`}
                          data-testid={`ai-theme-${key}-${i}`}
                          aria-label={`Use ${cfg.label} theme`}
                        >
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-white/20"
                            style={{ background: swatch }}
                          />
                          {cfg.label}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={async () => {
                      setSharingIdx(i);
                      await new Promise((res) => setTimeout(res, 60));
                      await shareRecipeAsImage({ current: storyRefs.current[i] }, r, themes[i] || "dark");
                      setSharingIdx(-1);
                    }}
                    className="btn-yellow"
                    aria-label="Share to Instagram Stories"
                    data-testid={`ai-recipe-share-${i}`}
                    disabled={sharingIdx === i}
                  >
                    {sharingIdx === i ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                    {sharingIdx === i ? "Preparing Story…" : "Share to Instagram Story"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StatsDashboard({ stats, analytics }) {
  const items = [
    { label: "Subscribers", value: formatNumber(stats?.subscribers || 0), icon: Users, color: "#FF0000" },
    { label: "Total views", value: formatNumber(stats?.total_views || 0), icon: Eye, color: "#E6FF00" },
    { label: "Videos", value: stats?.video_count || 0, icon: VideoIcon, color: "#FFFFFF" },
    { label: "Affiliate clicks", value: analytics?.total_clicks || 0, icon: TrendingUp, color: "#FF0000" },
  ];

  return (
    <section id="stats" className="px-6 lg:px-12 py-24 border-t border-white/10" data-testid="stats-section">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="overline mb-3">By the numbers</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight" data-testid="stats-heading">
            Real-time channel growth.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <div
              key={i}
              className="card-tech p-6 fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
              data-testid={`stat-card-${i}`}
            >
              <it.icon size={20} style={{ color: it.color }} />
              <div className="mt-6 font-display font-extrabold text-4xl">{it.value}</div>
              <div className="mt-1 text-sm text-[#71717a]">{it.label}</div>
            </div>
          ))}
        </div>

        {analytics?.top_links?.length > 0 && (
          <div className="mt-10 card-tech p-6" data-testid="top-links-card">
            <div className="overline mb-5">Top affiliate links</div>
            <div className="space-y-3">
              {analytics.top_links.map((l, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-[#71717a] w-6">#{i + 1}</span>
                    <span className="text-[#e5e5e5]">{l.title}</span>
                  </div>
                  <span className="font-mono text-sm text-[#E6FF00]">{l.clicks} clicks</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function PartnerMarquee() {
  const partners = [
    { name: "Amazon", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/pxclwdxp_amazon-logo-png_seeklogo-286206.png", url: "https://amzn.to/4xYY1QW" },
    { name: "Flipkart", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/z8xsya0l_Flipkart-Logo-2015-present.webp", url: "https://fkrt.co/9ggBlN" },
    { name: "Myntra", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/n209cphq_myntra-logo-png_seeklogo-513444.png", url: "https://myntr.in/8JVrkJ" },
    { name: "AJIO", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/4csemkxn_Ajio-Logo1.jpg", url: "https://extp.in/iS45T8" },
    { name: "Yatra", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/ssz3dbtx_yatra-com-logo-png_seeklogo-362510.png", url: "https://extp.in/qbiCpa" },
    { name: "Oppo", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/kclcbyc6_oppo-logo-oppo-icon-free-free-vector.jpg", url: "https://extp.in/6sY1C1" },
    { name: "Cleartrip", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/aaxe939l_cleartrip-logo-png_seeklogo-498837.png", url: "https://extp.in/DfKzWO" },
    { name: "Skyscanner", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/u13hvhju_unnamed.png", url: "https://extp.in/SNgwd3" },
    { name: "NykaaFashion", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/rmvqnvnt_images.jpg", url: "https://extp.in/Wh5IDv" },
    { name: "Palmonas", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/18tgqov6_unnamed%20%281%29.png", url: "https://extp.in/ypA7HV" },
    { name: "Plum", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/x4b7c4l5_Plum.jpeg", url: "https://extp.in/e93P9R" },
    { name: "Meesho", logo: "https://customer-assets-4nw71qhi.emergentagent.net/job_subscriber-surge-23/artifacts/othlnjyu_images%20%281%29.jpg", url: "https://bitli.in/fiMilUa" },
  ];
  const logoLoop = [...partners, ...partners, ...partners];

  return (
    <section
      className="relative overflow-hidden border-y border-white/10"
      data-testid="partner-marquee"
    >
      {/* Row 1 — headline ticker (dark bg) */}
      <div className="bg-black py-4">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="flex items-center gap-6 whitespace-nowrap">
                <span className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-white">
                  UP TO <span className="text-[#FF0000]">75-80% OFF</span>
                </span>
                <span className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-[#E6FF00]">
                  ON TOP BRANDS
                </span>
                <span className="text-2xl text-[#FF0000]">●</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2 — brand logos (white bg, opposite direction) */}
      <div className="bg-white py-5">
        <div className="marquee">
          <div className="marquee-track" style={{ animationDirection: "reverse" }}>
            {logoLoop.map((p, i) => (
              <a
                key={i}
                href={p.url}
                onClick={(e) => openExternal(e, p.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-8 whitespace-nowrap cursor-pointer group"
                aria-label={p.name}
                data-testid={`marquee-logo-${p.name.toLowerCase()}-${i}`}
              >
                <img
                  src={safeImageUrl(p.logo)}
                  alt={p.name}
                  className="h-12 sm:h-14 w-auto object-contain group-hover:scale-110 transition-transform"
                />
                <span className="text-2xl text-[#FF0000] font-bold">●</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Newsletter({ settings }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're in. Welcome aboard.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-6 lg:px-12 py-24 border-t border-white/10" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="overline mb-3">Get notified</div>
          <h2 className="font-display font-extrabold text-5xl sm:text-7xl tracking-tighter leading-none">
            Never miss <br />
            a <span className="text-[#FF0000]">drop.</span>
          </h2>
          <p className="mt-6 text-[#a1a1aa] text-lg max-w-md">
            Weekly recipes, behind-the-scenes, and kitchen tips. Zero spam.
          </p>
        </div>
        <form onSubmit={submit} className="lg:col-span-5 space-y-4" data-testid="newsletter-form">
          <div className="flex gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourstudio.com"
              required
              className="input-dark"
              data-testid="newsletter-email-input"
            />
            <button type="submit" disabled={loading} className="btn-red" data-testid="newsletter-submit-btn">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
              Subscribe
            </button>
          </div>
          <p className="text-xs text-[#71717a] font-mono">
            By subscribing you agree to receive emails from {settings?.creator_name}.
          </p>
        </form>
      </div>
    </section>
  );
}

function AmazonBanner() {
  return (
    <section className="px-6 lg:px-12 py-16 border-t border-white/10" data-testid="amazon-banner-section">
      <div className="max-w-7xl mx-auto">
        <a
          href="https://amzn.to/4xYY1QW"
          onClick={(e) => openExternal(e, "https://amzn.to/4xYY1QW")}
          target="_blank"
          rel="noopener noreferrer"
          className="group block bg-white rounded-2xl p-10 lg:p-14 hover:scale-[1.01] transition-transform duration-300 glow-yellow cursor-pointer"
          data-testid="amazon-banner-link"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <div className="overline mb-3 text-black" style={{ color: "#FF9900" }}>
                Today&apos;s Pick
              </div>
              <h3 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-black max-w-xl">
                Featured deal on Amazon.in
              </h3>
              <p className="mt-3 text-zinc-600 text-base max-w-md">
                Tap the logo to grab today&apos;s hand-picked deal. New offer refreshed regularly.
              </p>
            </div>
            <img
              src={safeImageUrl("https://customer-assets.emergentagent.com/job_subscriber-surge-23/artifacts/ez7vy7ki_51HCHFclmmL.jpg")}
              alt="Amazon.in"
              className="h-20 sm:h-24 lg:h-28 w-auto object-contain group-hover:translate-x-1 transition-transform"
              data-testid="amazon-banner-logo"
            />
          </div>
        </a>
      </div>
    </section>
  );
}

function Footer({ settings }) {
  return (
    <footer className="px-6 lg:px-12 py-12 border-t border-white/10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#FF0000] flex items-center justify-center">
            <Play size={14} fill="white" stroke="white" />
          </div>
          <span className="font-display font-extrabold">{(settings?.creator_name || "Creator Hub").toUpperCase()}</span>
        </div>
        <div className="flex items-center gap-5 text-[#a1a1aa]">
          <a href={settings?.youtube_url || "#"} onClick={(e) => openExternal(e, settings?.youtube_url || "https://youtube.com")} target="_blank" rel="noopener noreferrer" className="hover:text-[#FF0000] transition cursor-pointer" data-testid="footer-youtube">
            <Youtube size={20} />
          </a>
          <a href="https://www.instagram.com/rasoirustic/" onClick={(e) => openExternal(e, "https://www.instagram.com/rasoirustic/")} target="_blank" rel="noopener noreferrer" className="hover:text-[#E4405F] transition cursor-pointer" data-testid="footer-instagram"><Instagram size={20} /></a>
          <a href={settings?.contact_email ? `mailto:${settings.contact_email}` : "mailto:rasoirustic@gmail.com"} className="hover:text-[#E6FF00] transition flex items-center gap-1.5 text-sm font-mono" data-testid="footer-email">
            <Mail size={16} /> {settings?.contact_email || "rasoirustic@gmail.com"}
          </a>
        </div>
        <div className="text-xs text-[#71717a] font-mono">
          © {new Date().getFullYear()} {settings?.creator_name} — Built for creators
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-white/5 flex items-center justify-center gap-4">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.open("https://www.sitelock.com/verify.php?site=rusticrasoi.in", "SiteLock", "width=600,height=600,left=160,top=170");
          }}
          data-testid="footer-sitelock-badge"
        >
          <img
            src="https://shield.sitelock.com/shield/rusticrasoi.in"
            alt="SiteLock"
            title="SiteLock"
            className="h-10 opacity-80 hover:opacity-100 transition"
          />
        </a>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [settings, setSettings] = useState({ creator_name: "Creator Hub", creator_bio: "" });
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [isDemo, setIsDemo] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [financialPartners, setFinancialPartners] = useState([]);
  const [travelPartners, setTravelPartners] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, st, v, a, an, fp, tp] = await Promise.all([
          api.get("/settings"),
          api.get("/youtube/stats"),
          api.get("/youtube/videos"),
          api.get("/affiliates"),
          api.get("/analytics/summary"),
          api.get("/financial-partners"),
          api.get("/travel-partners"),
        ]);
        setSettings(s.data);
        setStats(st.data);
        setVideos(v.data.videos);
        setIsDemo(v.data.is_demo);
        setAffiliates(a.data);
        setAnalytics(an.data);
        setFinancialPartners(fp.data);
        setTravelPartners(tp.data);
      } catch (e) {
        console.error("Failed to load", e);
      }
    })();
  }, []);

  const airlines = (travelPartners.length ? travelPartners : DEFAULT_AIRLINES.concat(DEFAULT_HOTELS)).filter((p) => p.category === "airline" || (!p.category && DEFAULT_AIRLINES.some((x) => x.name === p.name)));
  const hotels = (travelPartners.length ? travelPartners : DEFAULT_AIRLINES.concat(DEFAULT_HOTELS)).filter((p) => p.category === "hotel" || (!p.category && DEFAULT_HOTELS.some((x) => x.name === p.name)));

  return (
    <div data-testid="landing-page">
      <Header settings={settings} />
      <Hero stats={stats} settings={settings} />
      <PartnerMarquee />
      <FinancialPartnersSection partners={financialPartners} />
      <TravelPartnersSection airlines={airlines} />
      <HotelsPartnersSection hotels={hotels} />
      <VideosGrid videos={videos} isDemo={isDemo} />
      <AffiliateShowcase affiliates={affiliates} />
      <AiGenerator />
      <FlyStayTicker airlines={airlines} hotels={hotels} />
      <StatsDashboard stats={stats} analytics={analytics} />
      <Newsletter settings={settings} />
      <AmazonBanner />
      <Footer settings={settings} />
    </div>
  );
}
